import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { fmtCurrency, fmtNumber, relativeTime } from "@/lib/utils";
import { AlertTriangle, ShieldCheck, Zap, DollarSign, Activity, PowerOff, Power } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

export default function BudgetPage() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState([]);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [killSwitch, setKillSwitch] = useState(null);
  const [killBusy, setKillBusy] = useState(false);
  const [outreach, setOutreach] = useState(null);
  const [outreachBusy, setOutreachBusy] = useState(false);
  const [pushover, setPushover] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);

  const load = async () => {
    const [s, u, k, p, o] = await Promise.all([
      api.get("/budget"),
      api.get("/budget/usage?limit=30"),
      api.get("/system/kill-switch").catch(() => ({ data: null })),
      api.get("/system/pushover/status").catch(() => ({ data: null })),
      api.get("/system/outreach").catch(() => ({ data: null })),
    ]);
    setData(s.data);
    setUsage(u.data.usage || []);
    setKillSwitch(k.data);
    setPushover(p.data);
    setOutreach(o.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const toggleOutreach = async () => {
    setOutreachBusy(true);
    try {
      const next = !outreach?.active;
      if (next && !window.confirm("Resume autonomous outreach? The sequencer will start sending LinkedIn invites, DMs and cold emails to eligible prospects.")) {
        setOutreachBusy(false);
        return;
      }
      const { data } = await api.post("/system/outreach", { active: next });
      setOutreach(data);
      toast.success(next ? "🚀 Autonomous outreach ACTIVE — sequencer will send" : "⏸ Autonomous outreach PAUSED — AI tools still work");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle outreach");
    }
    setOutreachBusy(false);
  };

  const toggleKill = async () => {
    setKillBusy(true);
    try {
      const next = !killSwitch?.active;
      const reason = next
        ? (window.prompt("Reason for activating the kill switch? (optional, helps audit)") || "")
        : undefined;
      const { data } = await api.post("/system/kill-switch", {
        active: next, reason,
      });
      setKillSwitch(data);
      toast.success(next ? "🛑 Kill switch ACTIVE — all AI flows paused" : "✅ AI flows resumed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle kill switch");
    }
    setKillBusy(false);
  };

  const testPushover = async () => {
    setPushBusy(true);
    try {
      await api.post("/system/pushover/test");
      toast.success("✅ Test push sent — check your phone");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pushover test failed");
    }
    setPushBusy(false);
  };

  const runAlertsNow = async () => {
    setPushBusy(true);
    try {
      await api.post("/system/usage-alerts/run-now");
      toast.success("Alert pass triggered — check Pushover for any due notifications");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Alert pass failed");
    }
    setPushBusy(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of ["daily_usd", "monthly_usd", "daily_runs"]) {
        if (edit[k] !== undefined && edit[k] !== "") {
          const v = k === "daily_runs" ? parseInt(edit[k]) : parseFloat(edit[k]);
          if (Number.isFinite(v)) payload[k] = v;
        }
      }
      if (edit.hard_stop_enabled !== undefined) payload.hard_stop_enabled = edit.hard_stop_enabled;
      if (Object.keys(payload).length === 0) { toast.info("Nothing to save"); setSaving(false); return; }
      await api.put("/budget", payload);
      toast.success("Caps updated");
      setEdit({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  if (!data) return <DashboardLayout title="..."><div className="text-sm text-muted-foreground">Loading...</div></DashboardLayout>;

  const { caps, burn, by_agent, daily } = data;
  const pctDaily = caps.daily_usd === -1 ? 0 : Math.min(100, Math.round(burn.today.usd / caps.daily_usd * 100));
  const pctMonth = caps.monthly_usd === -1 ? 0 : Math.min(100, Math.round(burn.month.usd / caps.monthly_usd * 100));
  const pctRuns = caps.daily_runs === -1 ? 0 : Math.min(100, Math.round(burn.today.runs / caps.daily_runs * 100));

  const dailyChart = daily.map((d) => ({ day: d.day.slice(5), usd: d.usd, runs: d.runs }));

  return (
    <DashboardLayout
      eyebrow="LLM Cost Governance"
      title="AI Budget"
      actions={
        <div className="flex items-center gap-2">
          <span className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">Plan</span>
          <span className="signal-pill capitalize">{caps.plan}</span>
        </div>
      }
    >
      {/* GLOBAL KILL SWITCH — prominent emergency stop for ALL LLM + flows */}
      <div
        className={`mb-6 rounded-sm border-2 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 ${
          killSwitch?.active
            ? "border-destructive bg-destructive/15"
            : "border-border bg-card"
        }`}
        data-testid="kill-switch-panel"
      >
        <div className="flex items-start gap-3">
          {killSwitch?.active ? (
            <PowerOff className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
          ) : (
            <Power className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <div className="font-display font-semibold flex items-center gap-2">
              {killSwitch?.active ? (
                <>
                  <span className="text-destructive">🛑 ALL AI FLOWS PAUSED</span>
                  <span className="text-[0.6rem] tracking-widest uppercase font-mono px-1.5 py-0.5 border border-destructive/40 text-destructive rounded-sm">
                    Kill switch active
                  </span>
                </>
              ) : (
                "Global AI Kill Switch — armed & ready"
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 max-w-xl">
              {killSwitch?.active ? (
                <>
                  All GPT-5.2 / Claude / Gemini calls return 503 — this also pauses
                  target-list generation and AI drafting. Use ONLY for emergencies.
                  Click to resume.
                  {killSwitch.reason && (
                    <div className="italic mt-1">Reason: &quot;{killSwitch.reason}&quot;</div>
                  )}
                  {killSwitch.activated_at && (
                    <div className="text-[0.65rem] mt-0.5">
                      Activated {relativeTime(killSwitch.activated_at)} by {killSwitch.set_by || "—"}
                    </div>
                  )}
                </>
              ) : (
                "Emergency brake — stops EVERY LLM call (drafting + generation too). To only pause auto-sending, use the Autonomous Outreach toggle below instead."
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={toggleKill}
          disabled={killBusy || killSwitch?.env_override}
          variant={killSwitch?.active ? "default" : "destructive"}
          className={`min-w-[180px] ${killSwitch?.active ? "bg-emerald-500 hover:bg-emerald-500/90 text-white" : ""}`}
          data-testid="kill-switch-toggle"
          title={killSwitch?.env_override ? "Locked by env var LLM_KILL_SWITCH — unset in deployment to override" : ""}
        >
          {killBusy ? "..." : killSwitch?.active ? (
            <><Power className="w-4 h-4 mr-1.5" /> Resume AI flows</>
          ) : (
            <><PowerOff className="w-4 h-4 mr-1.5" /> STOP ALL AI</>
          )}
        </Button>
      </div>

      {/* AUTONOMOUS OUTREACH — pause ONLY the sequencer's sending (AI tools keep working) */}
      <div
        className={`mb-6 rounded-sm border-2 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 ${
          outreach?.active ? "border-emerald-500/50 bg-emerald-500/10" : "border-amber-accent/40 bg-amber-accent/5"
        }`}
        data-testid="outreach-panel"
      >
        <div className="flex items-start gap-3">
          <Zap className={`w-6 h-6 flex-shrink-0 mt-0.5 ${outreach?.active ? "text-emerald-500" : "text-amber-accent"}`} />
          <div>
            <div className="font-display font-semibold flex items-center gap-2">
              {outreach?.active ? (
                <span className="text-emerald-500">🚀 Autonomous outreach ACTIVE</span>
              ) : (
                <span className="text-amber-accent">⏸ Autonomous outreach PAUSED</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 max-w-xl">
              {outreach?.active
                ? "The sequencer is sending LinkedIn invites → DMs → cold emails to eligible prospects. Pause anytime."
                : "The sequencer will NOT send anything. You can still generate target lists, source leads, and draft messages — turn this on only when you're ready to let outreach run."}
              {outreach?.set_by && (
                <div className="text-[0.65rem] mt-0.5">
                  Last changed by {outreach.set_by}
                  {outreach.active && outreach.activated_at ? ` · ${relativeTime(outreach.activated_at)}` : ""}
                  {!outreach.active && outreach.paused_at ? ` · ${relativeTime(outreach.paused_at)}` : ""}
                </div>
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={toggleOutreach}
          disabled={outreachBusy}
          variant={outreach?.active ? "destructive" : "default"}
          className={`min-w-[180px] ${outreach?.active ? "" : "bg-emerald-500 hover:bg-emerald-500/90 text-white"}`}
          data-testid="outreach-toggle"
        >
          {outreachBusy ? "..." : outreach?.active ? (
            <><PowerOff className="w-4 h-4 mr-1.5" /> Pause outreach</>
          ) : (
            <><Power className="w-4 h-4 mr-1.5" /> Start outreach</>
          )}
        </Button>
      </div>

      {/* PUSHOVER NOTIFICATIONS panel */}
      <div className="mb-6 data-card flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="pushover-panel">
        <div className="flex items-start gap-3">
          <div className={`w-2 h-2 rounded-full mt-2 ${pushover?.configured ? "bg-emerald-500" : "bg-amber-accent"}`} />
          <div>
            <div className="font-display font-semibold">
              Pushover alerts {pushover?.configured ? <span className="text-emerald-500 text-xs ml-1">· configured</span> : <span className="text-amber-accent text-xs ml-1">· not configured</span>}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 max-w-xl">
              {pushover?.configured ? (
                <>You&apos;ll get push notifications for: <strong>end-of-day USD spend</strong>, <strong>end-of-month total</strong>, and <strong>token-burn anomalies</strong> (last-hour spend &gt; 3× your 7-day hourly average). Emergency alerts include a &quot;Kill Switch&quot; deep-link.</>
              ) : (
                <>Set <code className="px-1 bg-input rounded text-[0.65rem]">PUSHOVER_API_TOKEN</code> and <code className="px-1 bg-input rounded text-[0.65rem]">PUSHOVER_USER_KEY</code> in deployment secrets to enable. Get them at <a href="https://pushover.net" target="_blank" rel="noopener noreferrer" className="text-signal-500 hover:underline">pushover.net</a> (free trial, $5 one-time per device).</>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={runAlertsNow}
            disabled={!pushover?.configured || pushBusy}
            variant="outline"
            size="sm"
            data-testid="alerts-run-now-btn"
          >
            Run alert check now
          </Button>
          <Button
            onClick={testPushover}
            disabled={!pushover?.configured || pushBusy}
            variant="outline"
            size="sm"
            data-testid="pushover-test-btn"
          >
            {pushBusy ? "..." : "Send test push"}
          </Button>
        </div>
      </div>

      {/* Status banner */}
      {pctDaily >= 80 && (
        <div className={`mb-6 flex items-start gap-3 px-4 py-3 rounded-sm border ${
          pctDaily >= 100 ? "border-destructive/40 bg-destructive/10 text-destructive"
                          : "border-amber-accent/40 bg-amber-accent/10 text-amber-accent"
        }`} data-testid="budget-alert">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">
              {pctDaily >= 100 ? "Daily AI budget reached" : `Daily AI budget at ${pctDaily}%`}
            </div>
            <div className="text-xs opacity-90">
              ${burn.today.usd.toFixed(2)} of ${caps.daily_usd.toFixed(2)} today.{" "}
              {pctDaily >= 100 && caps.hard_stop_enabled && "New agent calls will return 429 until reset (00:00 UTC)."}
              {pctDaily >= 100 && !caps.hard_stop_enabled && "Hard stop is OFF — calls continue but you're over budget."}
            </div>
          </div>
        </div>
      )}

      {/* Burn KPIs with progress bars */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <BurnCard label="Today" current={burn.today.usd} cap={caps.daily_usd} pct={pctDaily} icon={DollarSign} testid="budget-burn-daily" />
        <BurnCard label="This month" current={burn.month.usd} cap={caps.monthly_usd} pct={pctMonth} icon={DollarSign} testid="budget-burn-month" />
        <BurnCard label="Runs today" current={burn.today.runs} cap={caps.daily_runs} pct={pctRuns} icon={Activity} unit="" testid="budget-burn-runs" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Caps editor */}
        <section className="data-card">
          <div className="label-eyebrow mb-2 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" />Workspace caps</div>
          <h3 className="font-display font-semibold text-lg mb-4">Spending limits</h3>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Daily USD cap (use -1 for unlimited)</Label>
              <Input data-testid="budget-cap-daily-usd" type="number" step="0.01" placeholder={String(caps.daily_usd)}
                value={edit.daily_usd ?? ""} onChange={(e) => setEdit({ ...edit, daily_usd: e.target.value })} />
              <div className="text-[0.65rem] text-muted-foreground mt-1 font-mono">
                Current: ${caps.daily_usd === -1 ? "∞" : caps.daily_usd.toFixed(2)} ({caps.source.daily_usd})
              </div>
            </div>
            <div>
              <Label className="text-xs">Monthly USD cap</Label>
              <Input data-testid="budget-cap-monthly-usd" type="number" step="0.01" placeholder={String(caps.monthly_usd)}
                value={edit.monthly_usd ?? ""} onChange={(e) => setEdit({ ...edit, monthly_usd: e.target.value })} />
              <div className="text-[0.65rem] text-muted-foreground mt-1 font-mono">
                Current: ${caps.monthly_usd === -1 ? "∞" : caps.monthly_usd.toFixed(2)} ({caps.source.monthly_usd})
              </div>
            </div>
            <div>
              <Label className="text-xs">Daily AI run count cap</Label>
              <Input data-testid="budget-cap-daily-runs" type="number" placeholder={String(caps.daily_runs)}
                value={edit.daily_runs ?? ""} onChange={(e) => setEdit({ ...edit, daily_runs: e.target.value })} />
              <div className="text-[0.65rem] text-muted-foreground mt-1 font-mono">
                Current: {caps.daily_runs === -1 ? "∞" : caps.daily_runs} ({caps.source.daily_runs})
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <div>
                <Label className="text-sm">Hard stop when caps hit</Label>
                <div className="text-[0.65rem] text-muted-foreground">Return 429 to client when over budget. Off = soft warn only.</div>
              </div>
              <Switch
                data-testid="budget-hard-stop"
                checked={edit.hard_stop_enabled ?? caps.hard_stop_enabled}
                onCheckedChange={(v) => setEdit({ ...edit, hard_stop_enabled: v })}
              />
            </div>
            <Button onClick={save} disabled={saving} data-testid="budget-save-btn" className="rounded-sm w-full">{saving ? "Saving..." : "Save caps"}</Button>
          </div>
        </section>

        {/* By-agent cost */}
        <section className="data-card">
          <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Zap className="w-3 h-3" />30-day spend by agent</div>
          <h3 className="font-display font-semibold text-lg mb-4">Cost attribution</h3>
          {by_agent.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-8">No AI runs in the last 30 days.</div>
          ) : (
            <ul className="space-y-2">
              {by_agent.map((b, i) => {
                const pct = by_agent[0].usd ? (b.usd / by_agent[0].usd) * 100 : 0;
                return (
                  <li key={b.agent_type} data-testid={`budget-agent-${b.agent_type}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="capitalize font-medium">{b.agent_type}</span>
                      <span className="font-mono text-xs">{fmtCurrency(b.usd)} · {fmtNumber(b.runs)} runs</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-sm overflow-hidden">
                      <div className="h-full bg-signal-500" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {/* 14d chart */}
      <section className="data-card mt-6">
        <div className="label-eyebrow mb-2">Daily spend (14d)</div>
        <h3 className="font-display font-semibold text-lg mb-4">Burn trend</h3>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailyChart} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }}
                formatter={(v, n) => n === "usd" ? `$${v}` : v} />
              <Bar dataKey="usd" fill="hsl(var(--chart-1))" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Recent calls */}
      <section className="data-card mt-6">
        <div className="label-eyebrow mb-2">Recent LLM calls</div>
        <div className="max-h-80 overflow-y-auto">
          {usage.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">No calls yet.</div>}
          <ul className="space-y-1.5 text-xs font-mono">
            {usage.map((u, i) => (
              <li key={u.id} className="grid grid-cols-12 gap-2 border-b border-border last:border-0 py-1.5" data-testid={`budget-call-${i}`}>
                <div className="col-span-3 truncate"><span className="text-signal-500 capitalize">{u.agent_type}</span></div>
                <div className="col-span-2 text-muted-foreground">{u.model}</div>
                <div className="col-span-2 text-right">{fmtNumber(u.tokens_total)} tok</div>
                <div className="col-span-2 text-right text-signal-500">${u.cost_usd.toFixed(4)}</div>
                <div className="col-span-3 text-right text-muted-foreground">{relativeTime(u.ts)}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="mt-8 p-4 border border-border rounded-sm bg-muted/20 flex items-start gap-3 text-xs text-muted-foreground">
        <ShieldCheck className="w-4 h-4 text-signal-500 flex-shrink-0 mt-0.5" />
        <div>
          <strong className="text-foreground">Cost model:</strong> blended {Object.entries(data.model_costs).map(([m, v]) => `${m} @ $${v}/1k`).join(" · ")}. Tokens are rough (char/4) estimates — accurate to within ~10% of provider billing.
        </div>
      </div>
    </DashboardLayout>
  );
}

function BurnCard({ label, current, cap, pct, icon: Icon, unit = "$", testid }) {
  const isUnlimited = cap === -1;
  const display = unit === "$" ? `$${(current).toFixed(2)}` : current;
  const capDisplay = isUnlimited ? "∞" : (unit === "$" ? `$${cap.toFixed(2)}` : cap);
  const barColor = pct >= 100 ? "bg-destructive" : pct >= 80 ? "bg-amber-accent" : "bg-signal-500";

  return (
    <div className="data-card" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-3"><Icon className="w-3 h-3 text-muted-foreground" /><span className="label-eyebrow">{label}</span></div>
      <div className="flex items-end gap-2">
        <div className="text-3xl font-display font-semibold tracking-tightest">{display}</div>
        <div className="text-xs text-muted-foreground mb-1.5">/ {capDisplay}</div>
      </div>
      {!isUnlimited && (
        <>
          <div className="mt-3 h-1.5 bg-muted rounded-sm overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
          <div className="mt-1 text-[0.65rem] text-muted-foreground font-mono">{pct}% used</div>
        </>
      )}
      {isUnlimited && <div className="mt-3 text-[0.65rem] tracking-widest uppercase text-signal-500">Unlimited</div>}
    </div>
  );
}
